// Offline Mode Service
// Uses IndexedDB to store orders when offline and syncs when back online

const DB_NAME = 'mise-pos-offline';
const DB_VERSION = 2;
const ORDERS_STORE = 'pendingOrders';
const PAYMENTS_STORE = 'pendingPayments';
const MENU_CACHE_STORE = 'cachedMenu';

interface PendingOrder {
  id: string;
  checkId: string;
  tableId: string;
  items: any[];
  // Preserve the course mode: replaying a held/coursed order as
  // fire-immediately would send everything to the kitchen at once
  fireImmediately?: boolean;
  createdAt: string;
  status: 'pending' | 'syncing' | 'failed';
  retries: number;
  error?: string;
}

interface PendingPayment {
  id: string;
  checkId: string;
  tableId?: string;
  splitCheckId?: string;
  method: string;
  amountCents: number;
  tipCents: number;
  amountTenderedCents?: number;
  createdAt: string;
  status: 'pending' | 'syncing' | 'failed';
  retries: number;
  error?: string;
}

interface CachedMenu {
  id: string;
  restaurantId: string;
  categories: any[];
  items: any[];
  modifierGroups: any[];
  cachedAt: string;
  expiresAt: string;
}

class OfflineService {
  private db: IDBDatabase | null = null;
  private isOnline: boolean = navigator.onLine;
  private syncInProgress: boolean = false;
  private listeners: Set<(online: boolean) => void> = new Set();

  constructor() {
    this.init();
    this.setupNetworkListeners();
  }

  private async init() {
    return new Promise<void>((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => {
        console.error('Failed to open IndexedDB:', request.error);
        reject(request.error);
      };

      request.onsuccess = () => {
        this.db = request.result;
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;

        // Create orders store
        if (!db.objectStoreNames.contains(ORDERS_STORE)) {
          const ordersStore = db.createObjectStore(ORDERS_STORE, { keyPath: 'id' });
          ordersStore.createIndex('status', 'status', { unique: false });
          ordersStore.createIndex('createdAt', 'createdAt', { unique: false });
        }

        // Create payments store
        if (!db.objectStoreNames.contains(PAYMENTS_STORE)) {
          const paymentsStore = db.createObjectStore(PAYMENTS_STORE, { keyPath: 'id' });
          paymentsStore.createIndex('status', 'status', { unique: false });
          paymentsStore.createIndex('createdAt', 'createdAt', { unique: false });
        }

        // Create menu cache store
        if (!db.objectStoreNames.contains(MENU_CACHE_STORE)) {
          const menuStore = db.createObjectStore(MENU_CACHE_STORE, { keyPath: 'id' });
          menuStore.createIndex('restaurantId', 'restaurantId', { unique: false });
          menuStore.createIndex('cachedAt', 'cachedAt', { unique: false });
        }
      };
    });
  }

  private setupNetworkListeners() {
    window.addEventListener('online', () => {
      this.isOnline = true;
      this.notifyListeners(true);
      this.syncPendingItems();
    });

    window.addEventListener('offline', () => {
      this.isOnline = false;
      this.notifyListeners(false);
    });

    // Safety net: the 'online' event can fire while the server is still
    // unreachable (wifi up, internet down) — retry anything queued every 30s.
    setInterval(() => {
      if (this.isOnline) this.syncPendingItems();
    }, 30_000);
  }

  public onNetworkChange(callback: (online: boolean) => void) {
    this.listeners.add(callback);
    // Call immediately with current status
    callback(this.isOnline);
    return () => this.listeners.delete(callback);
  }

  private notifyListeners(online: boolean) {
    this.listeners.forEach((cb) => cb(online));
  }

  public getOnlineStatus(): boolean {
    return this.isOnline;
  }

  // Store an order for offline sync
  public async queueOrder(order: Omit<PendingOrder, 'status' | 'retries'>): Promise<string> {
    if (!this.db) await this.init();

    const pendingOrder: PendingOrder = {
      ...order,
      status: 'pending',
      retries: 0,
    };

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([ORDERS_STORE], 'readwrite');
      const store = transaction.objectStore(ORDERS_STORE);
      const request = store.add(pendingOrder);

      request.onsuccess = () => {
        resolve(order.id);
        // Try to sync immediately if online
        if (this.isOnline) {
          this.syncPendingItems();
        }
      };

      request.onerror = () => reject(request.error);
    });
  }

  // Store a payment for offline sync
  public async queuePayment(payment: Omit<PendingPayment, 'status' | 'retries'>): Promise<string> {
    if (!this.db) await this.init();

    const pendingPayment: PendingPayment = {
      ...payment,
      status: 'pending',
      retries: 0,
    };

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([PAYMENTS_STORE], 'readwrite');
      const store = transaction.objectStore(PAYMENTS_STORE);
      const request = store.add(pendingPayment);

      request.onsuccess = () => {
        resolve(payment.id);
        if (this.isOnline) {
          this.syncPendingItems();
        }
      };

      request.onerror = () => reject(request.error);
    });
  }

  // Get all pending items
  public async getPendingOrders(): Promise<PendingOrder[]> {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([ORDERS_STORE], 'readonly');
      const store = transaction.objectStore(ORDERS_STORE);
      const request = store.getAll();

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  public async getPendingPayments(): Promise<PendingPayment[]> {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([PAYMENTS_STORE], 'readonly');
      const store = transaction.objectStore(PAYMENTS_STORE);
      const request = store.getAll();

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  public async getPendingCount(): Promise<{ orders: number; payments: number }> {
    const orders = await this.getPendingOrders();
    const payments = await this.getPendingPayments();
    // 'failed' items are retried automatically, so they still count as waiting
    const waiting = (s: string) => s === 'pending' || s === 'failed' || s === 'syncing';
    return {
      orders: orders.filter((o) => waiting(o.status)).length,
      payments: payments.filter((p) => waiting(p.status)).length,
    };
  }

  // ===========================================
  // SYNC (replay everything taken while offline)
  // ===========================================

  private apiUrl(): string {
    return import.meta.env.VITE_API_URL || 'https://gleaming-elegance-production-d593.up.railway.app/api';
  }

  private authHeaders(): Record<string, string> {
    let token = '';
    try {
      token = JSON.parse(localStorage.getItem('mise-pos-auth') || '{}')?.state?.token || '';
    } catch {
      // no session — sync will retry after login
    }
    return { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };
  }

  // Sync pending items when back online. Retries 'failed' items too — a
  // single blip during a previous sync must not strand an order or payment.
  public async syncPendingItems(): Promise<void> {
    if (!this.isOnline || this.syncInProgress) return;

    this.syncInProgress = true;
    // Local check keys resolved this pass (so an order and its payment queued
    // against the same offline table land on the SAME real check)
    const resolvedChecks = new Map<string, string>();

    try {
      const retryable = (s: string) => s === 'pending' || s === 'failed';

      // Orders first (so the check has its items before payment closes it)
      const orders = await this.getPendingOrders();
      for (const order of orders.filter((o) => retryable(o.status))) {
        await this.syncOrder(order, resolvedChecks);
      }

      const payments = await this.getPendingPayments();
      for (const payment of payments.filter((p) => retryable(p.status))) {
        await this.syncPayment(payment, resolvedChecks);
      }
    } finally {
      this.syncInProgress = false;
    }
  }

  private static readonly UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

  // Orders/payments queued before a check existed carry a local placeholder
  // key instead of a real check id. Resolve it: reuse the table's open check
  // if there is one, otherwise open a new check for that table.
  private async resolveCheckId(
    localKey: string,
    tableId: string | undefined,
    resolvedChecks: Map<string, string>
  ): Promise<string> {
    if (OfflineService.UUID_RE.test(localKey)) return localKey;

    const cached = resolvedChecks.get(localKey);
    if (cached) return cached;

    if (!tableId) throw new Error('Offline item has no table to attach to');

    // Reuse the table's live check if one exists (open or partially paid)
    for (const status of ['open', 'partial_payment']) {
      const listRes = await fetch(`${this.apiUrl()}/v1/checks?status=${status}&tableId=${tableId}`, {
        headers: this.authHeaders(),
      });
      if (listRes.ok) {
        const listBody = await listRes.json();
        const live = (listBody?.data || [])[0];
        if (live?.id) {
          resolvedChecks.set(localKey, live.id);
          return live.id;
        }
      }
    }

    // Otherwise open one
    const openRes = await fetch(`${this.apiUrl()}/v1/checks`, {
      method: 'POST',
      headers: this.authHeaders(),
      body: JSON.stringify({ tableId, partySize: 1, type: 'table' }),
    });
    if (!openRes.ok) throw new Error(`Could not open check (${openRes.status})`);
    const openBody = await openRes.json();
    const newId = openBody?.data?.id;
    if (!newId) throw new Error('Could not open check');
    resolvedChecks.set(localKey, newId);
    return newId;
  }

  private async syncOrder(order: PendingOrder, resolvedChecks: Map<string, string>): Promise<void> {
    try {
      await this.updateOrderStatus(order.id, 'syncing');

      const checkId = await this.resolveCheckId(order.checkId, order.tableId, resolvedChecks);

      const response = await fetch(`${this.apiUrl()}/v1/orders`, {
        method: 'POST',
        headers: this.authHeaders(),
        body: JSON.stringify({
          checkId,
          fireImmediately: order.fireImmediately !== false,
          // idempotencyKey = queue id: if a previous attempt landed but the
          // response was lost, the server returns the existing order instead
          // of creating a duplicate.
          idempotencyKey: order.id,
          items: order.items.map((item: any) => ({
            menuItemId: item.menuItemId,
            quantity: item.quantity,
            seatNumber: item.seatNumber,
            courseNumber: item.courseNumber,
            modifiers: item.modifiers,
            specialInstructions: item.specialInstructions,
          })),
        }),
      });

      if (response.ok) {
        await this.removeOrder(order.id);
      } else {
        throw new Error(`API error: ${response.status}`);
      }
    } catch (error: any) {
      // Mark as failed — retried on the next sync pass
      await this.updateOrderStatus(order.id, 'failed', error.message);
    }
  }

  private async syncPayment(payment: PendingPayment, resolvedChecks: Map<string, string>): Promise<void> {
    try {
      await this.updatePaymentStatus(payment.id, 'syncing');

      // Only cash can be recorded after the fact — a card that isn't present
      // anymore can't be charged. (The checkout screen doesn't queue card
      // payments offline for exactly this reason.)
      if (payment.method !== 'cash') {
        await this.updatePaymentStatus(payment.id, 'failed', 'Card payments need a connection');
        return;
      }

      const checkId = await this.resolveCheckId(payment.checkId, payment.tableId, resolvedChecks);

      const response = await fetch(`${this.apiUrl()}/v1/payments/cash`, {
        method: 'POST',
        headers: this.authHeaders(),
        body: JSON.stringify({
          checkId,
          splitCheckId: payment.splitCheckId,
          amountTendered: payment.amountTenderedCents || payment.amountCents,
          tipAmount: payment.tipCents,
          idempotencyKey: payment.id,
        }),
      });

      if (response.ok) {
        await this.removePayment(payment.id);
      } else {
        throw new Error(`API error: ${response.status}`);
      }
    } catch (error: any) {
      await this.updatePaymentStatus(payment.id, 'failed', error.message);
    }
  }

  private async updateOrderStatus(id: string, status: PendingOrder['status'], error?: string): Promise<void> {
    if (!this.db) return;

    const transaction = this.db.transaction([ORDERS_STORE], 'readwrite');
    const store = transaction.objectStore(ORDERS_STORE);
    const request = store.get(id);

    request.onsuccess = () => {
      const order = request.result;
      if (order) {
        order.status = status;
        order.retries = (order.retries || 0) + (status === 'failed' ? 1 : 0);
        if (error) order.error = error;
        store.put(order);
      }
    };
  }

  private async updatePaymentStatus(id: string, status: PendingPayment['status'], error?: string): Promise<void> {
    if (!this.db) return;

    const transaction = this.db.transaction([PAYMENTS_STORE], 'readwrite');
    const store = transaction.objectStore(PAYMENTS_STORE);
    const request = store.get(id);

    request.onsuccess = () => {
      const payment = request.result;
      if (payment) {
        payment.status = status;
        payment.retries = (payment.retries || 0) + (status === 'failed' ? 1 : 0);
        if (error) payment.error = error;
        store.put(payment);
      }
    };
  }

  private async removeOrder(id: string): Promise<void> {
    if (!this.db) return;

    return new Promise((resolve) => {
      const transaction = this.db!.transaction([ORDERS_STORE], 'readwrite');
      const store = transaction.objectStore(ORDERS_STORE);
      store.delete(id);
      transaction.oncomplete = () => resolve();
    });
  }

  private async removePayment(id: string): Promise<void> {
    if (!this.db) return;

    return new Promise((resolve) => {
      const transaction = this.db!.transaction([PAYMENTS_STORE], 'readwrite');
      const store = transaction.objectStore(PAYMENTS_STORE);
      store.delete(id);
      transaction.oncomplete = () => resolve();
    });
  }

  // ===========================================
  // MENU CACHING
  // ===========================================

  /**
   * Cache menu data for offline use
   * @param restaurantId - Restaurant ID
   * @param menuData - Menu data (categories, items, modifiers)
   * @param ttlHours - Time to live in hours (default 24)
   */
  public async cacheMenu(
    restaurantId: string,
    menuData: { categories: any[]; items: any[]; modifierGroups: any[] },
    ttlHours: number = 24
  ): Promise<void> {
    if (!this.db) await this.init();

    const cachedMenu: CachedMenu = {
      id: `menu-${restaurantId}`,
      restaurantId,
      categories: menuData.categories,
      items: menuData.items,
      modifierGroups: menuData.modifierGroups,
      cachedAt: new Date().toISOString(),
      expiresAt: new Date(Date.now() + ttlHours * 60 * 60 * 1000).toISOString(),
    };

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([MENU_CACHE_STORE], 'readwrite');
      const store = transaction.objectStore(MENU_CACHE_STORE);
      const request = store.put(cachedMenu);

      request.onsuccess = () => {
        // Also cache in service worker
        if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
          navigator.serviceWorker.controller.postMessage({
            type: 'CACHE_MENU',
            menuData: cachedMenu,
          });
        }
        resolve();
      };
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Get cached menu for a restaurant
   * @param restaurantId - Restaurant ID
   * @returns Cached menu or null if not found/expired
   */
  public async getCachedMenu(
    restaurantId: string
  ): Promise<{ categories: any[]; items: any[]; modifierGroups: any[] } | null> {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([MENU_CACHE_STORE], 'readonly');
      const store = transaction.objectStore(MENU_CACHE_STORE);
      const request = store.get(`menu-${restaurantId}`);

      request.onsuccess = () => {
        const cachedMenu = request.result as CachedMenu | undefined;

        if (!cachedMenu) {
          resolve(null);
          return;
        }

        // Check if expired
        if (new Date(cachedMenu.expiresAt) < new Date()) {
          // Clean up expired cache
          this.clearMenuCache(restaurantId);
          resolve(null);
          return;
        }

        resolve({
          categories: cachedMenu.categories,
          items: cachedMenu.items,
          modifierGroups: cachedMenu.modifierGroups,
        });
      };

      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Clear cached menu for a restaurant
   */
  public async clearMenuCache(restaurantId: string): Promise<void> {
    if (!this.db) return;

    return new Promise((resolve) => {
      const transaction = this.db!.transaction([MENU_CACHE_STORE], 'readwrite');
      const store = transaction.objectStore(MENU_CACHE_STORE);
      store.delete(`menu-${restaurantId}`);
      transaction.oncomplete = () => resolve();
    });
  }

  /**
   * Get menu cache info
   */
  public async getMenuCacheInfo(
    restaurantId: string
  ): Promise<{ cachedAt: string; expiresAt: string } | null> {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([MENU_CACHE_STORE], 'readonly');
      const store = transaction.objectStore(MENU_CACHE_STORE);
      const request = store.get(`menu-${restaurantId}`);

      request.onsuccess = () => {
        const cachedMenu = request.result as CachedMenu | undefined;
        if (cachedMenu) {
          resolve({
            cachedAt: cachedMenu.cachedAt,
            expiresAt: cachedMenu.expiresAt,
          });
        } else {
          resolve(null);
        }
      };
      request.onerror = () => reject(request.error);
    });
  }
}

// Export singleton instance
export const offlineService = new OfflineService();

// React hook for network status
import { useState, useEffect } from 'react';

export function useOnlineStatus() {
  const [isOnline, setIsOnline] = useState(offlineService.getOnlineStatus());
  const [pendingCount, setPendingCount] = useState({ orders: 0, payments: 0 });

  useEffect(() => {
    const unsubscribe = offlineService.onNetworkChange(setIsOnline);

    // Check pending count periodically
    const checkPending = async () => {
      const count = await offlineService.getPendingCount();
      setPendingCount(count);
    };

    checkPending();
    const interval = setInterval(checkPending, 5000);

    return () => {
      unsubscribe();
      clearInterval(interval);
    };
  }, []);

  return { isOnline, pendingCount };
}
